import axios from 'axios';
import Cookies from 'js-cookie';
import history from '../history';
import { toastr } from 'react-redux-toastr';
import { logoutCookie } from '../../utils';

// Import store to dispatch logout action
import { store, persistor } from '../../redux/stores/index';
import { logout } from '../../redux/actions/logout';

// Configure the default axios instance globally
axios.defaults.baseURL = process.env.REACT_APP_BASE_URL;
axios.defaults.timeout = 30000; // 30 seconds timeout

// Create a configured axios instance
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  timeout: 30000, // 30 seconds timeout
});

// Request interceptor to add auth headers (for both default and instance)
const requestInterceptor = (config) => {
  // Add auth headers from cookies
  const accessToken = Cookies.get('access_token');
  const uid = Cookies.get('uid');
  const client = Cookies.get('client');
  
  if (accessToken && uid && client) {
    config.headers['access-token'] = accessToken;
    config.headers['uid'] = uid;
    config.headers['client'] = client;
  }
  
  config.headers['Accept'] = 'application/json';
  config.headers['Content-Type'] = 'application/json';
  config.headers['is_consumer'] = 'true';
  
  return config;
};

const requestErrorInterceptor = (error) => {
  return Promise.reject(error);
};

// Response interceptor to handle common HTTP errors (for both default and instance)
const responseInterceptor = (response) => {
  return response;
};

const responseErrorInterceptor = (error) => {
  if (error.response) {
    switch (error.response.status) {
      case 401:
        // Skip 401 handling for login and signup APIs
        const isLoginOrSignupAPI = error.config.url && (
          error.config.url.includes('/auth/sign_in') ||
          error.config.url.includes('/sign_up') ||
          error.config.url.includes('/validate_user') ||
          error.config.url.includes('/web_login_with_otp') ||
          error.config.url.includes('/customer_web_login_with_email') ||
          error.config.url.includes('/social_login/sign_up') ||
          error.config.url.includes('/providers/update_mobile_number_without_login') ||
          error.config.url.includes('/validate_password_otp')
        );
        
        if (isLoginOrSignupAPI) {
          // Don't redirect for login/signup APIs, let them show their own errors
          return Promise.reject(error);
        }
        
        // Clear all auth cookies using the existing logout function
        logoutCookie();
        
        // Clear Redux state by dispatching logout action
        store.dispatch(logout());
        
        // Clear persisted Redux state
        persistor.purge();
        
        // Show user-friendly notification
        toastr.error('Session Expired', 'Your session has expired. Please login again.');
        
        // Force page reload and redirect directly to login
        window.location.href = '/login';
        break;
        
      case 403:
        // toastr.error('Access Denied', 'You do not have permission to access this resource.');
        break;
        
      case 404:
        // toastr.error('Not Found', 'The requested resource was not found.');
        break;
        
      case 500:
        // toastr.error('Server Error', 'An internal server error occurred. Please try again later.');
        break;
        
      default:
        // Handle other errors silently or with a generic message
        if (error.response.status >= 500) {
          toastr.error('Server Error', 'Something went wrong. Please try again later.');
        }
        break;
    }
  } else if (error.request) {
    // Network error
    toastr.error('Network Error', 'Please check your internet connection and try again.');
  }
  
  return Promise.reject(error);
};

// Apply interceptors to default axios instance
axios.interceptors.request.use(requestInterceptor, requestErrorInterceptor);
axios.interceptors.response.use(responseInterceptor, responseErrorInterceptor);

// Apply interceptors to configured axios instance
axiosInstance.interceptors.request.use(requestInterceptor, requestErrorInterceptor);
axiosInstance.interceptors.response.use(responseInterceptor, responseErrorInterceptor);

// Export the configured axios instance
export default axiosInstance; 